/**
 * Utah Home Health Agency Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * - Primary State Regulation: Utah Administrative Code R432-700 (Home Health Agency Rule)
 * - Background Screening: R432-35 (DACS - Direct Access Clearance System)
 * - General Licensing: R432-2 (General Licensing Provisions)
 * - CNA Training: R432-45 (Nurse Aide Training and Competency Evaluation Program)
 * - Statutory Authority: Utah Code 26B-2-202
 * - Vulnerable Adult Abuse: Utah Code 62A-3-305
 * 
 * REGULATORY BODY:
 * Division of Licensing and Background Checks (DLBC)
 * Utah Department of Health and Human Services
 * 26 South 500 West, Salt Lake City, UT 84101
 * Phone: (801) 538-4242
 * Website: https://dlbc.utah.gov/
 * 
 * KEY VERIFIED REQUIREMENTS (December 2024):
 * - CNA Training: 100 hours per R432-45-5(6) [NOT 75 hours]
 * - PCA In-Service: 6 hours/year per R432-700-22(2)(g)
 * - CNA In-Service: 12 hours/year per R432-45-5
 * - Plan of Care Review: Every 60 days per R432-700-23(4-5)
 * - RN Supervision: Every 2 weeks (skilled) / 3 months (maintenance) per R432-700-20(9)(l)
 * - QA Committee Review: Quarterly per R432-700-19(4)
 * - Background Check: DACS system per R432-35
 * - Fingerprints: Within 15 working days per R432-35-4(2)(b)
 * - Record Retention: 7 years per R432-200-29(e)
 * - Administrator: 1 year managerial/supervisory experience per R432-700-8(1)
 * - CNA Age: 18 years old per R432-700-21(2)(a)
 * - CNA Certificate: Within 6 months of hire per R432-700-21(2)(b)
 * - CNA CPR: Required per R432-700-21(2)(c)
 * - PCA Age: 18 years old per R432-700-22(1)
 * - APS Hotline: 1-800-371-7897
 * 
 * Last Verified: December 2024 against R432-700, R432-35, R432-45
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const UTAH_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'UT-A1',
    itemId: 'A1',
    stateCode: 'UT',
    title: 'DLBC Home Health Agency License Status',
    description: 'Utah requires all home health agencies to be licensed by the Division of Licensing and Background Checks per R432-700-6',
    severity: 'high',
    regulationCode: 'R432-700-6, R432-2',
    regulationName: 'Licensure',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from Utah DLBC?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R432-700-6(2): The licensee shall comply with Rule R432-2, General Licensing Provisions.',
      },
      {
        id: 'q2',
        question: 'What is your home health agency license expiration date?',
        inputType: 'date',
        helpText: 'Found on your DLBC license certificate. Licenses are renewed annually.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your DLBC Home Health Agency license is current and active per R432-700-6.',
      atRisk: 'Your license expires within 90 days. Submit renewal application to DLBC before expiration.',
      nonCompliant: 'Operating without a valid DLBC license violates R432-700-6 and Utah Code 26B-2-202. Contact DLBC immediately at (801) 538-4242.',
      unknown: 'Verify your license status with DLBC at (801) 538-4242.',
    },
    stateNotes: [
      'Licenses renewed annually per R432-2',
      'Submit renewal 60 days before expiration',
      'DLBC conducts unannounced inspections',
      'Background screening via DACS per R432-35',
    ],
  },

  {
    id: 'UT-A2',
    itemId: 'A2',
    stateCode: 'UT',
    title: 'Governing Body Requirements',
    description: 'Utah requires organized governing body with full legal responsibility per R432-700-7',
    severity: 'high',
    regulationCode: 'R432-700-7',
    regulationName: 'Governing Body and Policies',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have required governing body elements?',
        inputType: 'checklist',
        options: [
          { value: 'org_chart', label: 'Organization chart showing administrative structure - R432-700-7(2)(a)' },
          { value: 'compliance', label: 'Responsibility for federal, state, local law compliance - R432-700-7(2)(b)' },
          { value: 'non_discrimination', label: 'Non-discrimination policy (race, color, sex, religion, ancestry, national origin) - R432-700-7(2)(c)' },
          { value: 'policies', label: 'Policies describing functions/services and protecting client rights - R432-700-7(2)(d)' },
          { value: 'annual_eval', label: 'Review written annual evaluation from administrator - R432-700-7(2)(e)' },
          { value: 'resources', label: 'Provide resources for safe working environment - R432-700-7(2)(f)' },
          { value: 'financial', label: 'System of financial management and accountability - R432-700-7(2)(g)' },
          { value: 'admin_appoint', label: 'Written appointment of qualified administrator - R432-700-7(3)(h)' },
        ],
        required: true,
        helpText: 'Per R432-700-7(1): Governing body assumes full legal responsibility for conduct of the home health agency.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Governing body structure meets R432-700-7 requirements.',
      atRisk: 'Governing body may be missing some required elements.',
      nonCompliant: 'Governing body structure is inadequate per R432-700-7.',
      unknown: 'Review governing body requirements against R432-700-7.',
    },
    stateNotes: [
      'Governing body must develop and implement bylaws per R432-700-7(3)',
      'Bylaws must include statement of purpose, officer qualifications, conflict of interest policy',
      'Annually required meetings per R432-700-7(3)(g)',
    ],
  },

  {
    id: 'UT-A3',
    itemId: 'A3',
    stateCode: 'UT',
    title: 'Administrator Qualifications',
    description: 'Utah requires qualified administrator with at least 1 year supervisory experience per R432-700-8',
    severity: 'high',
    regulationCode: 'R432-700-8',
    regulationName: 'Administrator Responsibilities',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your administrator have at least one year of managerial or supervisory experience?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R432-700-8(1): The administrator shall have at least one year of managerial or supervisory experience.',
      },
      {
        id: 'q2',
        question: 'Do you have a written designation of someone to act in administrator\'s absence?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-8(2)(a): Designate in writing a qualified person to act in absence.',
      },
      {
        id: 'q3',
        question: 'Is the administrator or designee available during the agency\'s hours of operation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-8(2)(c): Administrator or designee is available during operating hours.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator meets R432-700-8 qualification requirements.',
      atRisk: 'Administrator qualifications may not fully meet Utah requirements.',
      nonCompliant: 'Administrator does not meet Utah requirements per R432-700-8(1).',
      unknown: 'Verify administrator qualifications against R432-700-8.',
    },
    stateNotes: [
      'Administrator must have 1 year managerial/supervisory experience per R432-700-8(1)',
      'Written designee required for absences per R432-700-8(2)(a)',
      'Administrator reviews policies annually per R432-700-8(2)(e)',
      'Current written designations maintained in agency per R432-700-8(2)(l)',
    ],
  },

  {
    id: 'UT-A4',
    itemId: 'A4',
    stateCode: 'UT',
    title: 'Personnel Policies',
    description: 'Utah requires written personnel policies and procedures per R432-700-9',
    severity: 'high',
    regulationCode: 'R432-700-9',
    regulationName: 'Personnel',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written personnel policies addressing required elements?',
        inputType: 'checklist',
        options: [
          { value: 'job_desc', label: 'Job descriptions, qualifications, validation of licensure/certificates - R432-700-9(2)(a)' },
          { value: 'orientation', label: 'Orientation for direct and contract employees - R432-700-9(2)(b)' },
          { value: 'evaluations', label: 'Criteria for and frequency of performance evaluations - R432-700-9(2)(c)' },
          { value: 'schedules', label: 'Work schedules, payment method/period, benefits - R432-700-9(2)(d)' },
          { value: 'inservice', label: 'Frequency and documentation of in-service training - R432-700-9(2)(e)' },
          { value: 'personnel_files', label: 'Contents of personnel files - R432-700-9(2)(f)' },
          { value: 'after_hours', label: 'Emergency and after-hours care policies available to client - R432-700-9(2)(g)' },
          { value: 'abuse_training', label: 'Annual abuse reporting training documented - R432-700-9(4)' },
        ],
        required: true,
        helpText: 'Per R432-700-9(2): Administrator shall develop written policies and procedures.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel policies meet R432-700-9 requirements.',
      atRisk: 'Some personnel policies may be missing.',
      nonCompliant: 'Personnel policies are inadequate per R432-700-9.',
      unknown: 'Review personnel policies against R432-700-9.',
    },
    stateNotes: [
      'Staff must be trained annually on abuse reporting per R432-700-9(4)',
      'Employees licensed/certified as required by DOPL per R432-700-9(3)',
      'Qualified personnel competent to perform duties per R432-700-9(1)',
    ],
  },

  {
    id: 'UT-A5',
    itemId: 'A5',
    stateCode: 'UT',
    title: 'Quality Assurance Program',
    description: 'Utah requires annual governing body review and quarterly interdisciplinary QA committee per R432-700-19',
    severity: 'high',
    regulationCode: 'R432-700-19',
    regulationName: 'Quality Assurance',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your governing body conduct annual review of quality, appropriateness, and scope of services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R432-700-19(1): Reviewed and evaluated annually by governing body.',
      },
      {
        id: 'q2',
        question: 'Does your administrator submit a written annual evaluation report to the governing body?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-19(2): Administrator shall conduct annual evaluation and submit written report.',
      },
      {
        id: 'q3',
        question: 'Does your interdisciplinary QA committee meet quarterly?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - quarterly with written report' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-19(4): Evaluate client services on a quarterly basis.',
      },
      {
        id: 'q4',
        question: 'Does your QA committee have at least 3 members from 3 different health care professions?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-19(4)(b): Minimum 3 members representing 3 different licensed/certified professions.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      // No administrator written evaluation per R432-700-19(2)
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality assurance program meets R432-700-19 requirements.',
      atRisk: 'Quality assurance program may be incomplete. Administrator must submit written annual evaluation per R432-700-19(2).',
      nonCompliant: 'Quality assurance program is inadequate per R432-700-19.',
      unknown: 'Review QA program against R432-700-19.',
    },
    stateNotes: [
      'Governing body annual review per R432-700-19(1)',
      'Administrator annual written report per R432-700-19(2)',
      'QA committee quarterly review per R432-700-19(4)',
      'QA committee: 3+ members from 3+ professions per R432-700-19(4)(b)',
      'Review active and closed records per R432-700-19(5)(a)',
    ],
  },

  {
    id: 'UT-A6',
    itemId: 'A6',
    stateCode: 'UT',
    title: 'Incident Reporting (Utah Code 62A-3-305)',
    description: 'Utah requires mandatory reporting of vulnerable adult abuse, neglect, exploitation',
    severity: 'high',
    regulationCode: '62A-3-305',
    regulationName: 'Reporting Requirements - Vulnerable Adults',
    agencyName: 'Utah Adult Protective Services',
    agencyAbbr: 'APS',
    agencyPhone: '1-800-371-7897',
    agencyUrl: 'https://daas.utah.gov/adult-protective-services/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written incident reporting policy that includes mandatory APS reporting?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per Utah Code 62A-3-305(1): Any individual with reason to believe vulnerable adult is subject to abuse, neglect, or exploitation shall immediately report.',
      },
      {
        id: 'q2',
        question: 'Are all staff trained on mandatory reporting requirements?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff trained annually' },
          { value: 'most', label: 'Most staff trained' },
          { value: 'no', label: 'No formal training' },
        ],
        helpText: 'Per R432-700-9(4): Staff trained annually on abuse reporting requirements.',
      },
      {
        id: 'q3',
        question: 'Do staff know the APS hotline number (1-800-371-7897)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
        helpText: 'Reports must be made immediately to Adult Protective Services.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'most', result: 'AT_RISK' },
      // Staff don't know APS hotline
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident reporting policy meets Utah Code 62A-3-305 requirements.',
      atRisk: 'Not all staff trained on mandatory reporting or staff may not know APS hotline (1-800-371-7897). All staff must be trained annually.',
      nonCompliant: 'No incident reporting policy. Failure to report is a Class B misdemeanor.',
      unknown: 'Review reporting requirements under Utah Code 62A-3-305.',
    },
    stateNotes: [
      'Immediate reporting to APS: 1-800-371-7897',
      'Failure to report: Class B misdemeanor per Utah Code 62A-3-305(9)(a)',
      'Reporter immunity per Utah Code 62A-3-305(7)',
      'Annual training required per R432-700-9(4)',
    ],
  },

  {
    id: 'UT-A7',
    itemId: 'A7',
    stateCode: 'UT',
    title: 'Record Retention',
    description: 'Utah requires 7-year record retention per R432-200-29',
    severity: 'medium',
    regulationCode: 'R432-200-29, R432-700-17',
    regulationName: 'Record Retention',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer (meets Utah requirement)' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Per R432-200-29(e): Records retained at least 7 years after last date of care.',
      },
      {
        id: 'q2',
        question: 'For minors, do you retain records until age 18 plus 2 additional years?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
          { value: 'na', label: 'N/A - no minor patients' },
        ],
        helpText: 'Per R432-200-29(f): Retain minor records until age 18 or majority plus 2 years.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: '7plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '5to6', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less5', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets Utah 7-year requirement per R432-200-29.',
      atRisk: 'Utah requires 7-year retention. Update your policy.',
      nonCompliant: 'Record retention does not meet Utah 7-year requirement.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      '7 years minimum per R432-200-29(e)',
      'Minors: until age 18 + 2 additional years per R432-200-29(f)',
      'Records protected against loss, defacement, tampering per R432-200-29(c)',
      'Protected against unauthorized access per R432-200-29(d)',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND SCREENING (DACS)
  // ============================================
  {
    id: 'UT-B1',
    itemId: 'B1',
    stateCode: 'UT',
    title: 'DACS Background Clearance (R432-35)',
    description: 'Utah requires Direct Access Clearance System (DACS) certification for all direct patient access staff',
    severity: 'high',
    regulationCode: 'R432-35-4',
    regulationName: 'Covered Provider - DACS Process',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/office-of-background-processing/dacs/',
    questions: [
      {
        id: 'q1',
        question: 'Do you use DACS to initiate certification for direct patient access before provisional license or engagement?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R432-35-4(1): Enter required information into DACS before issuance of provisional license or engagement.',
      },
      {
        id: 'q2',
        question: 'Do covered individuals sign criminal background screening authorization forms?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-35-4(2)(a): Authorization form signed and available for department review.',
      },
      {
        id: 'q3',
        question: 'Do covered individuals submit fingerprints within 15 working days of engagement?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-35-4(2)(b): Fingerprints within 15 working days of engagement.',
      },
      {
        id: 'q4',
        question: 'Do you update DACS within 5 working days of engagement or termination?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-35-4(3): DACS reflects current status within 5 working days.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'DACS background clearance process meets R432-35-4 requirements.',
      atRisk: 'DACS process has gaps. Review fingerprint timeline and DACS updates.',
      nonCompliant: 'Not using DACS properly. Staff with direct patient access require DACS certification.',
      unknown: 'Review DACS requirements at dlbc.utah.gov.',
    },
    stateNotes: [
      'DACS = Direct Access Clearance System per R432-35',
      'Provisional engagement allowed while certification pending per 26B-2-239',
      'Fingerprints within 15 working days per R432-35-4(2)(b)',
      'Update DACS within 5 working days of status change per R432-35-4(3)',
      'License renewal: run verification report via DACS per R432-35-4(9)',
      'Excluded individuals cannot have direct patient access per R432-35-4(6)',
    ],
  },

  {
    id: 'UT-B2',
    itemId: 'B2',
    stateCode: 'UT',
    title: 'DACS Exclusion Verification (R432-35-8)',
    description: 'DACS checks multiple sources including OIG exclusion list',
    severity: 'high',
    regulationCode: 'R432-35-8',
    regulationName: 'Exclusion from Direct Patient Access',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your DACS process verify against all required sources?',
        inputType: 'checklist',
        options: [
          { value: 'criminal', label: 'Criminal background (felonies, class A misdemeanors) - R432-35-8(1)' },
          { value: 'juvenile', label: 'Juvenile court records (if under age 28) - R432-35-8(2)' },
          { value: 'dopl', label: 'DOPL licensing/certification records - R432-35-8(3)(v)' },
          { value: 'oig', label: 'OIG List of Excluded Individuals and Entities - R432-35-8(3)(vi)' },
        ],
        required: true,
        helpText: 'DACS automatically checks these sources, but you must ensure all staff are processed through DACS.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'DACS exclusion verification meets R432-35-8 requirements.',
      atRisk: 'Ensure all staff processed through DACS for complete verification.',
      nonCompliant: 'Staff not verified through DACS may be excluded individuals.',
      unknown: 'Review DACS verification requirements.',
    },
    stateNotes: [
      'DACS checks criminal history, juvenile records, DOPL records, OIG exclusion list',
      'Felony or Class A misdemeanor convictions exclude from direct patient access per R432-35-8(1)(a)',
      'OIG LEIE checked per R432-35-8(3)(vi)',
    ],
  },

  // ============================================
  // SECTION C: HEALTH SURVEILLANCE
  // ============================================
  {
    id: 'UT-C1',
    itemId: 'C1',
    stateCode: 'UT',
    title: 'TB Screening (R432-700-10)',
    description: 'Utah requires TB testing per R388-804 Special Measures for Control of Tuberculosis',
    severity: 'high',
    regulationCode: 'R432-700-10(3), R388-804',
    regulationName: 'Health Surveillance - Tuberculosis',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees have TB testing within 2 weeks of initial hiring?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees tested within 2 weeks' },
          { value: 'most', label: 'Most employees, some may be delayed' },
          { value: 'no', label: 'No / not tracking' },
        ],
        required: true,
        helpText: 'Per R432-700-10(3)(a)(i): Skin-tested within two weeks of initial hiring.',
      },
      {
        id: 'q2',
        question: 'What testing method do you use?',
        inputType: 'select',
        options: [
          { value: 'mantoux', label: 'Mantoux Method (TST)' },
          { value: 'igra', label: 'FDA-approved in-vitro serologic test (IGRA)' },
          { value: 'both', label: 'Either method based on employee' },
          { value: 'none', label: 'No standard testing' },
        ],
        helpText: 'Per R432-700-10(3): Mantoux Method or other FDA approved in-vitro serologic test.',
      },
      {
        id: 'q3',
        question: 'Do you test employees after suspected TB exposure or symptom development?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-10(3)(a)(ii-iii): Test within 2 weeks of exposure or symptom development.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['mantoux', 'igra', 'both'] },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // Not testing after exposure per R432-700-10(3)(a)(ii-iii)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening meets R432-700-10(3) requirements.',
      atRisk: 'Some employees may not have timely TB screening.',
      nonCompliant: 'TB screening not being conducted per R432-700-10(3), including testing after exposure per R432-700-10(3)(a)(ii-iii).',
      unknown: 'Verify TB screening procedures.',
    },
    stateNotes: [
      'Testing within 2 weeks of hiring per R432-700-10(3)(a)(i)',
      'Testing after exposure or symptoms per R432-700-10(3)(a)(ii-iii)',
      'Exemption for known positive reactors per R432-700-10(3)(b)',
      'Follows R388-804 Special Measures for Control of Tuberculosis',
      'Report communicable diseases per R386-702-3',
    ],
  },

  {
    id: 'UT-C2',
    itemId: 'C2',
    stateCode: 'UT',
    title: 'Employee Health Screening (R432-700-10)',
    description: 'Utah requires health screening policy per R432-700-10',
    severity: 'medium',
    regulationCode: 'R432-700-10(1-2)',
    regulationName: 'Health Surveillance',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written policy for employee health screenings?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R432-700-10(1): Establish and implement policy for employee health screenings.',
      },
      {
        id: 'q2',
        question: 'Are immunization components developed in accordance with R386-702 Communicable Disease Rules?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
        helpText: 'Per R432-700-10(2): Health screening and immunization per R386-702.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Employee health screening policy meets R432-700-10 requirements.',
      atRisk: 'Health screening policy may not address all requirements.',
      nonCompliant: 'No health screening policy per R432-700-10.',
      unknown: 'Review health screening requirements.',
    },
    stateNotes: [
      'Policy identifies situations preventing satisfactory duty performance',
      'Immunizations per R386-702 Communicable Disease Rules',
      'Report communicable diseases to local health department per R432-700-10(4)',
    ],
  },

  // ============================================
  // SECTION D: TRAINING
  // ============================================
  {
    id: 'UT-D1',
    itemId: 'D1',
    stateCode: 'UT',
    title: 'Certified Nursing Aide Requirements (R432-700-21)',
    description: 'Utah requires CNAs to be 18+, certified within 6 months, and CPR certified',
    severity: 'high',
    regulationCode: 'R432-700-21(2), R432-45-5',
    regulationName: 'Certified Nursing Aide',
    agencyName: 'Utah Nursing Assistant Registry (UNAR)',
    agencyAbbr: 'UNAR',
    agencyPhone: '(801) 547-9947',
    agencyUrl: 'https://utahcnaregistry.com/',
    questions: [
      {
        id: 'q1',
        question: 'Are ALL your Certified Nursing Aides at least 18 years old?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R432-700-21(2)(a): CNA shall be at least 18 years old.',
      },
      {
        id: 'q2',
        question: 'Do ALL CNAs have certificate of completion within 6 months of hire date?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-21(2)(b): Certificate within 6 months of date of hire.',
      },
      {
        id: 'q3',
        question: 'Are ALL CNAs certified in CPR and emergency procedures?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-21(2)(c): Certified in CPR and emergency procedures.',
      },
      {
        id: 'q4',
        question: 'Do CNAs receive at least 12 hours of in-service training per year?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-45-5: CNAs require 12 hours of staff development training annually.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q4', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'CNA requirements meet R432-700-21 and R432-45-5.',
      atRisk: 'Some CNAs may not meet all requirements.',
      nonCompliant: 'CNAs not meeting Utah requirements.',
      unknown: 'Review CNA requirements.',
    },
    stateNotes: [
      'CNA age: 18+ per R432-700-21(2)(a)',
      'Certificate within 6 months of hire per R432-700-21(2)(b)',
      'CPR certified per R432-700-21(2)(c)',
      'CNA training: 100 hours per R432-45-5(6) [NATCEP requirement]',
      '12 hours annual in-service per R432-45-5',
      'Listed on Utah Nursing Assistant Registry (UNAR)',
    ],
  },

  {
    id: 'UT-D2',
    itemId: 'D2',
    stateCode: 'UT',
    title: 'Personal Care Aide Requirements (R432-700-22)',
    description: 'Utah requires PCAs to be 18+, receive training and 6 hours annual in-service',
    severity: 'high',
    regulationCode: 'R432-700-22',
    regulationName: 'Personal Care Aides',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Are ALL your Personal Care Aides at least 18 years old?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R432-700-22(1): PCA shall be 18 years old.',
      },
      {
        id: 'q2',
        question: 'Do PCAs receive required training elements?',
        inputType: 'checklist',
        options: [
          { value: 'written_instructions', label: 'Written instructions from supervisor - R432-700-22(2)(a)' },
          { value: 'policies', label: 'Know agency policies and procedures - R432-700-22(2)(c)' },
          { value: 'first_aid', label: 'First aid training - R432-700-22(2)(d)' },
          { value: 'orientation', label: 'Orientation and training in aspects of care - R432-700-22(2)(e)' },
          { value: 'competency', label: 'Demonstrate competency in personal care - R432-700-22(2)(f)' },
        ],
        helpText: 'Per R432-700-22(2): Licensee shall ensure PCAs meet these requirements.',
      },
      {
        id: 'q3',
        question: 'Do PCAs receive at least 6 hours of in-service training per calendar year?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-22(2)(g): Minimum 6 hours in-service per calendar year, prorated in first year.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'PCA requirements meet R432-700-22.',
      atRisk: 'PCA training may be incomplete.',
      nonCompliant: 'PCAs not meeting Utah requirements.',
      unknown: 'Review PCA requirements.',
    },
    stateNotes: [
      'PCA age: 18+ per R432-700-22(1)',
      'First aid training required per R432-700-22(2)(d)',
      'Competency demonstration required per R432-700-22(2)(f)',
      '6 hours annual in-service per R432-700-22(2)(g)',
      'Prorated in first year of employment',
      'PCAs only perform tasks in service agreement per R432-700-22(2)(b)',
    ],
  },

  {
    id: 'UT-D3',
    itemId: 'D3',
    stateCode: 'UT',
    title: 'Orientation Requirements (R432-700-11)',
    description: 'Utah requires documented orientation for all employees',
    severity: 'high',
    regulationCode: 'R432-700-11',
    regulationName: 'Orientation',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Is orientation documented in writing for each employee?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R432-700-11(1): Document in writing that each employee is oriented.',
      },
      {
        id: 'q2',
        question: 'Does orientation include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'functions', label: 'Functions of agency employees and relationships - R432-700-11(2)(a)' },
          { value: 'job_desc', label: 'Job descriptions - R432-700-11(2)(b)' },
          { value: 'scope', label: 'Duties trained/certified/licensed to perform - R432-700-11(2)(c)' },
          { value: 'ethics', label: 'Ethics, confidentiality, client rights - R432-700-11(2)(d)' },
          { value: 'community', label: 'Other community agencies including EMS - R432-700-11(2)(e)' },
          { value: 'continuing_ed', label: 'Continuing education opportunities - R432-700-11(2)(f)' },
          { value: 'abuse_reporting', label: 'Abuse, neglect, exploitation reporting requirements - R432-700-11(2)(g)' },
        ],
        helpText: 'Per R432-700-11(2): Orientation includes all listed elements.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation program meets R432-700-11 requirements.',
      atRisk: 'Orientation may be missing required elements.',
      nonCompliant: 'Orientation not documented per R432-700-11(1).',
      unknown: 'Review orientation requirements.',
    },
    stateNotes: [
      'Written documentation required per R432-700-11(1)',
      'Must cover abuse reporting requirements per R432-700-11(2)(g)',
      'Ethics, confidentiality, client rights per R432-700-11(2)(d)',
    ],
  },

  // ============================================
  // SECTION E: CARE DELIVERY
  // ============================================
  {
    id: 'UT-E1',
    itemId: 'E1',
    stateCode: 'UT',
    title: 'Plan of Care Requirements (R432-700-23)',
    description: 'Utah requires plan of care reviewed every 60 days per R432-700-23',
    severity: 'high',
    regulationCode: 'R432-700-23',
    regulationName: 'Plan of Care',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for every patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
        helpText: 'Per R432-700-23(1)(a-c): Establish plan of care for any care, services, or treatment provided.',
      },
      {
        id: 'q2',
        question: 'Is the plan of care reviewed at intervals not exceeding 60 days?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-23(4-5): Reviewed and approved at intervals not to exceed 60 days per 42 CFR.',
      },
      {
        id: 'q3',
        question: 'Do plans of care include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'name', label: 'Client name - R432-700-23(7)(a)' },
          { value: 'diagnoses', label: 'Diagnoses - R432-700-23(7)(b)' },
          { value: 'goals', label: 'Treatment goals in measurable terms - R432-700-23(7)(c)' },
          { value: 'services', label: 'Services, intervals, and by whom - R432-700-23(7)(d)' },
          { value: 'equipment', label: 'Medical equipment and supplies - R432-700-23(7)(e)' },
          { value: 'medications', label: 'Medications by licensed personnel - R432-700-23(7)(f)' },
          { value: 'diet', label: 'Diet/nutritional requirements - R432-700-23(7)(h)' },
          { value: 'safety', label: 'Safety measures - R432-700-23(7)(i)' },
          { value: 'instructions', label: 'Instructions to client and family - R432-700-23(7)(j)' },
          { value: 'dates', label: 'Date initiated and dates of review - R432-700-23(7)(k)' },
        ],
        helpText: 'Per R432-700-23(7): Plan of care must include all listed elements.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // Plan of care missing required elements per R432-700-23(7)
      { condition: 'countAtLeast', questionId: 'q3', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q3', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q3', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plan of care meets R432-700-23 requirements.',
      atRisk: 'Care plans may not be completed before services, reviewed every 60 days, or may be missing required elements per R432-700-23(7).',
      nonCompliant: 'Services provided without required plan of care or plan missing critical elements.',
      unknown: 'Review care plan procedures.',
    },
    stateNotes: [
      'Plan of care signed by licensed health care professional per R432-700-23(2)',
      'Review at intervals not exceeding 60 days per R432-700-23(4)',
      'Skilled services: primary care provider approval every 60 days per R432-700-23(5)',
      'Significant changes documented and reported per R432-700-23(6)',
    ],
  },

  {
    id: 'UT-E2',
    itemId: 'E2',
    stateCode: 'UT',
    title: 'RN Supervision Requirements (R432-700-20)',
    description: 'Utah requires RN supervisory visits at specific intervals per R432-700-20(9)(l)',
    severity: 'high',
    regulationCode: 'R432-700-20(9)(l)',
    regulationName: 'Nursing Services - Supervision',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does an RN make initial assessment visits?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R432-700-20(9)(l)(i): Supervisory visits for initial assessment.',
      },
      {
        id: 'q2',
        question: 'For skilled services clients, does an RN visit every 2 weeks?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-20(9)(l)(ii): Every 2 weeks for skilled services.',
      },
      {
        id: 'q3',
        question: 'For long-term maintenance clients, does an RN visit every 3 months?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-20(9)(l)(iii): Every 3 months for long-term maintenance.',
      },
      {
        id: 'q4',
        question: 'Does an RN visit any time there is a question of change in client condition?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-20(9)(l)(iv): Any time there is a question of condition change.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervision meets R432-700-20(9)(l) requirements.',
      atRisk: 'RN supervision intervals may not meet requirements.',
      nonCompliant: 'RN supervision is inadequate per R432-700-20(9)(l).',
      unknown: 'Review supervision requirements.',
    },
    stateNotes: [
      'Initial assessment visit per R432-700-20(9)(l)(i)',
      'Every 2 weeks for skilled services per R432-700-20(9)(l)(ii)',
      'Every 3 months for long-term maintenance per R432-700-20(9)(l)(iii)',
      'Any time condition change question per R432-700-20(9)(l)(iv)',
      'RN makes initial nursing evaluation visit per R432-700-20(9)(a)',
      'RN supervises CNAs per R432-700-20(9)(k)',
    ],
  },

  {
    id: 'UT-E3',
    itemId: 'E3',
    stateCode: 'UT',
    title: 'Client Records Requirements (R432-700-17)',
    description: 'Utah requires comprehensive client records per R432-700-17',
    severity: 'high',
    regulationCode: 'R432-700-17',
    regulationName: 'Client Records',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do client records include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'id_data', label: 'Identification data (name, address, age, DOB) - R432-700-17(5)(a)' },
          { value: 'relative', label: 'Name/address of nearest relative or responsible person - R432-700-17(5)(b)' },
          { value: 'physician', label: 'Primary care provider name and phone - R432-700-17(5)(c)' },
          { value: 'caregiver', label: 'Family member/person providing care at home - R432-700-17(5)(d)' },
          { value: 'care_plan', label: 'Written plan of care - R432-700-17(5)(e)' },
          { value: 'assessment', label: 'Signed/dated client assessment - R432-700-17(5)(f)' },
          { value: 'referral', label: 'Reasons for referral - R432-700-17(5)(g)' },
          { value: 'suitability', label: 'Statement of residence suitability - R432-700-17(5)(h)' },
          { value: 'consults', label: 'Documentation of phone consults/case conferences - R432-700-17(5)(i)' },
          { value: 'clinical_notes', label: 'Signed/dated clinical notes for each visit - R432-700-17(5)(j)' },
          { value: 'discharge', label: 'Written termination summary - R432-700-17(5)(k)' },
        ],
        required: true,
        helpText: 'Per R432-700-17(5): Each client record contains listed information.',
      },
      {
        id: 'q2',
        question: 'Is each service documented in the client record with signed/dated notes?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-17(4)(a-c): Each contact/service entered, dated, and authenticated.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client records meet R432-700-17 requirements.',
      atRisk: 'Client records may be missing required elements.',
      nonCompliant: 'Client records are incomplete per R432-700-17.',
      unknown: 'Audit client records against requirements.',
    },
    stateNotes: [
      'Records maintained in organized format per R432-700-17(2)',
      'Record identification system per R432-700-17(3)',
      'Each contact documented with signature/date per R432-700-17(4)(b)',
      'Skilled services require additional elements per R432-700-17(6)',
    ],
  },

  {
    id: 'UT-E4',
    itemId: 'E4',
    stateCode: 'UT',
    title: 'Client Rights (R432-700-15)',
    description: 'Utah requires client rights to be established and made available per R432-700-15',
    severity: 'high',
    regulationCode: 'R432-700-15',
    regulationName: 'Client Rights',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Are written client rights established and made available?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R432-700-15(1): Written rights made available to client, guardian, next of kin, sponsoring agency, representative payee, and public.',
      },
      {
        id: 'q2',
        question: 'Do you protect required client rights?',
        inputType: 'checklist',
        options: [
          { value: 'informed_rights', label: 'Informed of rights and rules - R432-700-15(3)(a)' },
          { value: 'informed_charges', label: 'Informed of services and charges - R432-700-15(3)(b)' },
          { value: 'informed_condition', label: 'Informed of health condition - R432-700-15(3)(c)' },
          { value: 'participate', label: 'Participate in planning, refuse experimental research - R432-700-15(3)(d)' },
          { value: 'refuse_treatment', label: 'Refuse treatment with informed consequences - R432-700-15(3)(e)' },
          { value: 'confidentiality', label: 'Confidential treatment of records - R432-700-15(3)(f)' },
          { value: 'dignity', label: 'Treated with respect and dignity - R432-700-15(3)(g)' },
          { value: 'education', label: 'Taught about required services - R432-700-15(3)(h)' },
          { value: 'competent_staff', label: 'Assured competent personnel - R432-700-15(3)(i)' },
          { value: 'identification', label: 'Proper identification from staff - R432-700-15(3)(j)' },
          { value: 'complaint', label: 'Information on complaint procedures - R432-700-15(3)(k)' },
        ],
        helpText: 'Per R432-700-15(3): Each client has at least these rights.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 9 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client rights meet R432-700-15 requirements.',
      atRisk: 'Client rights may not be fully protected.',
      nonCompliant: 'Written client rights not established per R432-700-15.',
      unknown: 'Review client rights requirements.',
    },
    stateNotes: [
      'Written rights available to public upon request',
      'Policy determines how rights distributed per R432-700-15(2)',
      'Rights documented in clinical record per R432-700-15(3)(a)',
    ],
  },

  // ============================================
  // SECTION F: CONTRACTS & SERVICES
  // ============================================
  {
    id: 'UT-F1',
    itemId: 'F1',
    stateCode: 'UT',
    title: 'Contracts with Other Providers (R432-700-12)',
    description: 'Utah requires written contracts with other service providers per R432-700-12',
    severity: 'medium',
    regulationCode: 'R432-700-12',
    regulationName: 'Contracts',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written contracts with other providers or independent contractors who provide client services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
          { value: 'na', label: 'N/A - no contracted services' },
        ],
        required: true,
        helpText: 'Per R432-700-12(1): Secure written contracts or agreements from other providers.',
      },
      {
        id: 'q2',
        question: 'Do contracts include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'dates', label: 'Effective and expiration dates - R432-700-12(3)(a)' },
          { value: 'services', label: 'Description of goods or services to be provided - R432-700-12(3)(b)' },
          { value: 'license', label: 'Copy of contractor\'s professional license - R432-700-12(3)(c)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per R432-700-12(3): Each contract shall include these elements.',
      },
      {
        id: 'q3',
        question: 'Do you arrange orientation for contractors to meet job expectations?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per R432-700-12(1): Arrange orientation to ensure contractor prepared to meet expectations.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'na', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Contracts with other providers meet R432-700-12 requirements.',
      atRisk: 'Contracts may be missing required elements.',
      nonCompliant: 'No written contracts with other providers per R432-700-12.',
      unknown: 'Review contract requirements.',
    },
    stateNotes: [
      'Contracts available for department review per R432-700-12(2)',
      'Orientation arranged for contractors per R432-700-12(1)',
      'Include effective/expiration dates, services description, license copy',
    ],
  },

  {
    id: 'UT-F2',
    itemId: 'F2',
    stateCode: 'UT',
    title: 'Medication and Treatment Orders (R432-700-24)',
    description: 'Utah requires signed orders for skilled treatment and medications per R432-700-24',
    severity: 'high',
    regulationCode: 'R432-700-24',
    regulationName: 'Medication and Treatment',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Is skilled treatment administered only by licensed personnel with signed orders?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R432-700-24(1): Skilled treatment only by licensed personnel per signed order.',
      },
      {
        id: 'q2',
        question: 'Are medication orders signed by authorized prescribers?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-24(2): Medication per signed orders from authorized prescriber.',
      },
      {
        id: 'q3',
        question: 'Are remote (telephone/fax) orders signed by the prescriber within 31 days?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-24(3)(a): Remote orders signed within 31 days.',
      },
      {
        id: 'q4',
        question: 'Are skilled service orders reviewed/renewed by the primary care provider every 60 days?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-24(7): Orders reviewed/renewed at intervals not exceeding 60 days.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      // Remote orders not signed within 31 days per R432-700-24(3)(a)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medication and treatment procedures meet R432-700-24 requirements.',
      atRisk: 'Some medication/treatment procedures may need improvement. Ensure remote orders are signed within 31 days per R432-700-24(3)(a).',
      nonCompliant: 'Medication/treatment orders not meeting R432-700-24.',
      unknown: 'Review medication/treatment order procedures.',
    },
    stateNotes: [
      'Skilled treatment: signed order required per R432-700-24(1)',
      'Remote orders: signed within 31 days per R432-700-24(3)(a)',
      'Remote orders: received only by authorized licensed personnel per R432-700-24(3)(b)',
      'Order renewal: every 60 days max per R432-700-24(7)',
      'Unlicensed staff medication delegation per R156-31B-701 per R432-700-24(5)',
    ],
  },

  {
    id: 'UT-F3',
    itemId: 'F3',
    stateCode: 'UT',
    title: 'Termination of Services (R432-700-14)',
    description: 'Utah requires proper discharge procedures and documentation per R432-700-14',
    severity: 'medium',
    regulationCode: 'R432-700-14',
    regulationName: 'Termination of Services Policies',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written policies for terminating services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R432-700-14: Licensee may discharge under specified circumstances.',
      },
      {
        id: 'q2',
        question: 'Is a discharge summary completed when services are terminated?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-14(2): Person supervising/coordinating care completes discharge summary.',
      },
      {
        id: 'q3',
        question: 'Does your discharge summary include required elements?',
        inputType: 'checklist',
        options: [
          { value: 'care_provided', label: 'Care or services provided - R432-700-17(5)(k)(i)' },
          { value: 'course', label: 'Course of care and services - R432-700-17(5)(k)(ii)' },
          { value: 'reason', label: 'Reason for discharge - R432-700-17(5)(k)(iii)' },
          { value: 'status', label: 'Client status at discharge - R432-700-17(5)(k)(iv)' },
          { value: 'referral', label: 'Name of agency/facility if referred/transferred - R432-700-17(5)(k)(v)' },
        ],
        helpText: 'Per R432-700-17(5)(k): Termination summary describes these elements.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q3', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Termination of services meets R432-700-14 requirements.',
      atRisk: 'Discharge documentation may be incomplete.',
      nonCompliant: 'No termination policies per R432-700-14.',
      unknown: 'Review discharge requirements.',
    },
    stateNotes: [
      'R432-700-14(1)(a-o) lists valid discharge circumstances',
      'Discharge summary by supervising person per R432-700-14(2)',
      'Written summary in client record per R432-700-17(5)(k)',
    ],
  },

  // ============================================
  // SECTION G: CONFIDENTIALITY & DOCUMENTATION
  // ============================================
  {
    id: 'UT-G1',
    itemId: 'G1',
    stateCode: 'UT',
    title: 'Confidentiality and Release of Information (R432-700-18)',
    description: 'Utah requires policies to safeguard records and protect confidentiality per R432-700-18',
    severity: 'high',
    regulationCode: 'R432-700-18',
    regulationName: 'Confidentiality and Release of Information',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have policies safeguarding client records?',
        inputType: 'checklist',
        options: [
          { value: 'safeguard', label: 'Safeguard against loss, destruction, unauthorized use - R432-700-18(1)(a)' },
          { value: 'release', label: 'Written procedures for use, release, removal of records - R432-700-18(1)(b)' },
          { value: 'consent', label: 'Written consent required for photos - R432-700-18(1)(b)' },
          { value: 'confidential', label: 'Records kept confidential, disclosed only to authorized persons - R432-700-18(1)(c)' },
          { value: 'dept_access', label: 'Authorized department representatives may review - R432-700-18(1)(d)' },
          { value: 'storage', label: 'Filing, safe storage, easy access - R432-700-18(1)(e)' },
        ],
        required: true,
        helpText: 'Per R432-700-18(1): Licensee shall develop and implement these policies.',
      },
      {
        id: 'q2',
        question: 'When referring a client, do you obtain written consent before releasing information?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-18(2): Release to another agency/facility only with written consent.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Confidentiality policies meet R432-700-18 requirements.',
      atRisk: 'Some confidentiality policies may be incomplete.',
      nonCompliant: 'Confidentiality policies inadequate per R432-700-18.',
      unknown: 'Review confidentiality requirements.',
    },
    stateNotes: [
      'Written consent for photos per R432-700-18(1)(b)',
      'Referral release requires written consent per R432-700-18(2)',
      'Department has access for compliance review per R432-700-18(1)(d)',
    ],
  },

  {
    id: 'UT-G2',
    itemId: 'G2',
    stateCode: 'UT',
    title: 'Record-Keeping Policies (R432-700-17)',
    description: 'Utah requires comprehensive record-keeping policies per R432-700-17',
    severity: 'medium',
    regulationCode: 'R432-700-17(1)',
    regulationName: 'Client Records',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do your record-keeping policies address required elements?',
        inputType: 'checklist',
        options: [
          { value: 'use', label: 'Use of client records by authorized staff' },
          { value: 'content', label: 'Content requirements' },
          { value: 'confidentiality', label: 'Confidentiality' },
          { value: 'retention', label: 'Retention' },
          { value: 'storage', label: 'Storage' },
        ],
        required: true,
        helpText: 'Per R432-700-17(1): Policies address use, content, confidentiality, retention, storage.',
      },
      {
        id: 'q2',
        question: 'Are records maintained in an organized format with identification system?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R432-700-17(2-3): Organized format with identification system to locate records.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record-keeping policies meet R432-700-17 requirements.',
      atRisk: 'Record-keeping policies may be incomplete.',
      nonCompliant: 'Record-keeping policies inadequate per R432-700-17.',
      unknown: 'Review record-keeping requirements.',
    },
    stateNotes: [
      'Policies address use, content, confidentiality, retention, storage per R432-700-17(1)',
      'Records in organized format per R432-700-17(2)',
      'Identification system per R432-700-17(3)',
    ],
  },

  // ============================================
  // SECTION H: MEDICARE/MEDICAID
  // ============================================
  {
    id: 'UT-H1',
    itemId: 'H1',
    stateCode: 'UT',
    title: 'Medicare Certification Status',
    description: 'Medicare certification requirements for home health agencies in Utah',
    severity: 'high',
    regulationCode: '42 CFR 484 / R432-700',
    regulationName: 'Medicare CoPs',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/Medicare/Provider-Enrollment-and-Certification/CertificationandComplianc/HHAs',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide Medicare-covered home health services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Utah Medicaid/Private Pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your agency currently Medicare certified?',
        inputType: 'select',
        options: [
          { value: 'certified', label: 'Yes, fully certified' },
          { value: 'pending', label: 'Certification pending' },
          { value: 'not_certified', label: 'Not Medicare certified' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'certified', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'not_certified', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medicare certification is current or not required for your services.',
      atRisk: 'Medicare certification is pending. Ensure all Conditions of Participation (42 CFR 484) are met.',
      nonCompliant: 'Providing Medicare services without certification violates federal requirements.',
      unknown: 'Verify Medicare certification status if providing Medicare-covered services.',
    },
    stateNotes: [
      'DLBC conducts surveys for CMS',
      '42 CFR 484 Conditions of Participation apply',
      'Plan of care review every 60 days aligns with federal requirements',
    ],
  },

  {
    id: 'UT-H2',
    itemId: 'H2',
    stateCode: 'UT',
    title: 'Utah Medicaid Provider Enrollment',
    description: 'Utah Medicaid provider enrollment requirements',
    severity: 'high',
    regulationCode: 'R414-1, R414-14',
    regulationName: 'Utah Medicaid Provider Enrollment',
    agencyName: 'Utah Department of Health - Medicaid',
    agencyAbbr: 'Utah Medicaid',
    agencyPhone: '(801) 538-6155',
    agencyUrl: 'https://medicaid.utah.gov/become-a-provider/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide Utah Medicaid-funded home health services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Medicare/Private Pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your agency enrolled as a Utah Medicaid provider?',
        inputType: 'select',
        options: [
          { value: 'enrolled', label: 'Yes, currently enrolled' },
          { value: 'pending', label: 'Enrollment pending' },
          { value: 'not_enrolled', label: 'Not enrolled' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'enrolled', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'not_enrolled', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Utah Medicaid provider enrollment is current.',
      atRisk: 'Medicaid enrollment is pending. Monitor status with Utah Medicaid.',
      nonCompliant: 'Providing Medicaid services without enrollment violates Utah regulations.',
      unknown: 'Verify Medicaid enrollment status if providing Utah Medicaid-funded services.',
    },
    stateNotes: [
      'Medicaid provider enrollment per R414-1',
      'Home health services per R414-14',
      'Personal Care Services (PCS) per R414-38',
    ],
  },

  {
    id: 'UT-H3',
    itemId: 'H3',
    stateCode: 'UT',
    title: 'Electronic Visit Verification (EVV)',
    description: 'EVV required for Medicaid-funded personal care services per 21st Century Cures Act',
    severity: 'high',
    regulationCode: '21st Century Cures Act / Utah EVV Requirements',
    regulationName: 'EVV',
    agencyName: 'Utah Department of Health - Medicaid',
    agencyAbbr: 'Utah Medicaid',
    agencyPhone: '(801) 538-6155',
    agencyUrl: 'https://medicaid.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Utah Medicaid-funded personal care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use a state-approved EVV system to document visits?',
        inputType: 'select',
        options: [
          { value: 'yes_compliant', label: 'Yes, fully implemented and compliant' },
          { value: 'yes_partial', label: 'Yes, but not for all visits' },
          { value: 'no', label: 'No EVV system in place' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_compliant', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV system is properly implemented for Utah Medicaid-funded services.',
      atRisk: 'EVV is not being used consistently for all Medicaid visits.',
      nonCompliant: 'Utah Medicaid personal care services require EVV per federal law.',
      unknown: 'If you provide Medicaid services, verify EVV compliance.',
    },
    stateNotes: [
      '21st Century Cures Act mandates EVV',
      'Personal Care Services (PCS) require EVV',
      'Contact Utah Medicaid for approved EVV vendors',
    ],
  },

  // ============================================
  // SECTION I: PROVIDER CODE OF CONDUCT
  // ============================================
  {
    id: 'UT-I1',
    itemId: 'I1',
    stateCode: 'UT',
    title: 'Provider Code of Conduct (R380-80)',
    description: 'Utah requires providers to protect clients from abuse, neglect, exploitation per R380-80',
    severity: 'high',
    regulationCode: 'R380-80-4, R380-80-5',
    regulationName: 'Provider Code of Conduct',
    agencyName: 'Division of Licensing and Background Checks',
    agencyAbbr: 'DLBC',
    agencyPhone: '(801) 538-4242',
    agencyUrl: 'https://dlbc.utah.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you protect clients from abuse, neglect, exploitation, and mistreatment?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per R380-80-4(1): Provider shall protect each client from abuse, neglect, exploitation, and mistreatment.',
      },
      {
        id: 'q2',
        question: 'Do all providers protect clients and instruct others to do the same?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per R380-80-5(4): Protect clients from harm, fraud, and actions compromising health/safety.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Provider Code of Conduct meets R380-80 requirements.',
      atRisk: 'Code of conduct may need strengthening.',
      nonCompliant: 'Not meeting provider code of conduct requirements per R380-80.',
      unknown: 'Review R380-80 Provider Code of Conduct.',
    },
    stateNotes: [
      'R380-80-4(1): Protect clients from abuse, neglect, exploitation, mistreatment',
      'R380-80-5(4): Protect from harm, fraud, acts/omissions compromising safety',
      'Instruct and encourage others to do the same',
    ],
  },
];

export default UTAH_QUESTIONS;